import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useSelectedSchool } from '@/contexts/SelectedSchoolContext'

export interface InstructorSummary {
  membershipId: string
  userId: string
  joinedAt: string
  fullName: string | null
  email: string
  phone: string | null
  serviceArea: string | null
  relationshipType: 'employee' | 'contractor' | null
  hourlyRate: number | null
  jobTitle: string | null
  roleCategory: 'instructor' | 'administrator' | 'receptionist' | 'finance' | 'other' | null
  employmentType: 'full_time' | 'part_time' | 'casual' | null
  vehicleMake: string | null
  vehicleModel: string | null
  vehiclePlate: string | null
}

interface UseInstructorsReturn {
  instructors: InstructorSummary[]
  loading: boolean
  error: string | null
  refresh: () => void
}

/**
 * Lists the active instructors of the school currently selected in the school-admin
 * portal (SelectedSchoolContext, shared across Instructors/Students so switching schools
 * is consistent everywhere).
 *
 * The school id used here is UI state, not a trust boundary: every query below is itself
 * scoped by RLS ("memberships: admin read" / "profiles: school admin read", via the
 * is_school_admin_for() helper, migration 050) against the caller's own active OWNER/ADMIN
 * row — a manipulated or stale selectedSchoolId can at most ask for a school the caller
 * doesn't administer, which RLS answers with zero rows, never another school's data. An
 * admin with no selected school yet (none administered, or mid-onboarding) is a normal
 * empty state, not an error.
 */
export function useInstructors(): UseInstructorsReturn {
  const { selectedSchoolId } = useSelectedSchool()
  const [instructors, setInstructors] = useState<InstructorSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchInstructors = useCallback(async () => {
    if (!selectedSchoolId) { setInstructors([]); setLoading(false); return }
    setLoading(true)
    setError(null)

    try {
      // Active instructor memberships for the selected school.
      // RLS "memberships: admin read" allows this via is_school_admin_for(school_id).
      const { data: memberships, error: memErr } = await supabase
        .from('school_memberships')
        .select('id, user_id, joined_at, relationship_type, hourly_rate, job_title, role_category, employment_type')
        .eq('school_id', selectedSchoolId)
        .eq('role', 'INSTRUCTOR')
        .eq('status', 'active')
        .order('joined_at', { ascending: true })

      if (memErr) { setError('Could not load instructors.'); return }
      if (!memberships || memberships.length === 0) { setInstructors([]); return }

      const userIds = [...new Set(memberships.map(m => m.user_id))]

      // Public profiles. RLS "profiles: school admin read" allows this only for
      // ACTIVE members of a school this caller administers — matching the filter above.
      const { data: profiles, error: profErr } = await supabase
        .from('profiles')
        .select('id, full_name, email, phone')
        .in('id', userIds)

      if (profErr) { setError('Could not load instructor profiles.'); return }

      // Supplemental instructor fields (optional - absence must not break the list).
      // RLS "instructor_profiles: school admin read" mirrors the same access rule.
      const { data: details } = await supabase
        .from('instructor_profiles')
        .select('user_id, service_area, vehicle_make, vehicle_model, vehicle_plate')
        .in('user_id', userIds)

      const profileById = new Map((profiles ?? []).map(p => [p.id, p]))
      const detailById = new Map((details ?? []).map(d => [d.user_id, d]))

      const result: InstructorSummary[] = []
      const seen = new Set<string>()
      for (const m of memberships) {
        // A membership row whose profile didn't come back (deleted between queries, or no
        // longer active under RLS) is skipped rather than rendered with missing data.
        const p = profileById.get(m.user_id)
        if (!p) continue
        // school_memberships has a plain unique(user_id, school_id) constraint, so a duplicate
        // row for the same instructor in the same school cannot exist - this guard is defence
        // in depth against a future schema change, not a condition observed today.
        if (seen.has(m.user_id)) continue
        seen.add(m.user_id)

        result.push({
          membershipId: m.id,
          userId: m.user_id,
          joinedAt: m.joined_at,
          fullName: p.full_name,
          email: p.email,
          phone: p.phone,
          serviceArea: detailById.get(m.user_id)?.service_area ?? null,
          relationshipType: m.relationship_type,
          hourlyRate: m.hourly_rate === null || m.hourly_rate === undefined ? null : Number(m.hourly_rate),
          jobTitle: m.job_title ?? null,
          roleCategory: m.role_category ?? null,
          employmentType: m.employment_type ?? null,
          vehicleMake: detailById.get(m.user_id)?.vehicle_make ?? null,
          vehicleModel: detailById.get(m.user_id)?.vehicle_model ?? null,
          vehiclePlate: detailById.get(m.user_id)?.vehicle_plate ?? null,
        })
      }

      setInstructors(result)
    } finally {
      setLoading(false)
    }
  }, [selectedSchoolId])

  // Clear immediately on school change so the previous school's rows are never shown,
  // even briefly, while the new school's data is still loading.
  useEffect(() => { setInstructors([]) }, [selectedSchoolId])
  useEffect(() => { fetchInstructors() }, [fetchInstructors])

  return { instructors, loading, error, refresh: fetchInstructors }
}
